<template>
  <div class="block-calendar">
    <div class="calendar-panel">
      <div class="month-nav">
        <button @click="offset--">←</button>
        <span class="month-name">{{ monthName }}</span>
        <button @click="offset++">→</button>
      </div>
      <div class="weekdays">
        <span v-for="d in WEEKDAYS" :key="d">{{ d }}</span>
      </div>
      <div class="days">
        <span v-for="n in leadingBlanks" :key="'b' + n" class="blank" />
        <span
          v-for="d in daysInMonth"
          :key="d"
          class="day"
          :class="{ active: hasShow(d) }"
        >{{ d }}</span>
      </div>
    </div>
    <div class="shows-panel">
      <p class="shows-label">Esitykset</p>
      <ul class="shows-list">
        <li v-for="s in monthShows" :key="s.date + s.prodTitle">
          <a :href="s.link" class="show-row">
            <strong class="show-title">{{ s.prodTitle }}</strong>
            <span class="show-time">{{ showLabel(s.date) }} klo {{ s.time }}</span>
          </a>
        </li>
        <li v-if="!monthShows.length" class="no-shows">Ei esityksiä</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { useStore } from '@/KQL.js'

const WEEKDAYS = ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su']

const props = defineProps(['block'])
const kql = useStore()
const offset = ref(0)

const prods = computed(() => {
  const ids = props.block?.content?.pages || []
  return ids.map(id => pageFromPath(id, kql.pages)).filter(Boolean)
})

const allShows = computed(() =>
  prods.value.flatMap(prod =>
    yamlToArray(prod.performances).map(p => ({
      date: p.date,
      time: p.time,
      prodTitle: prod.title,
      link: pathName(prod.url),
    }))
  )
)

const base = computed(() => {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() + offset.value, 1)
})

const year = computed(() => base.value.getFullYear())
const month = computed(() => base.value.getMonth())

const monthName = computed(() =>
  capitalize(new Intl.DateTimeFormat('fi', { month: 'long', year: 'numeric' }).format(base.value))
)

const daysInMonth = computed(() => new Date(year.value, month.value + 1, 0).getDate())

const leadingBlanks = computed(() => {
  const first = new Date(year.value, month.value, 1).getDay()
  return (first + 6) % 7
})

const inCurrentMonth = (dateStr) => {
  const d = new Date(dateStr)
  return d.getFullYear() === year.value && d.getMonth() === month.value
}

const monthShows = computed(() =>
  allShows.value
    .filter(s => inCurrentMonth(s.date))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
)

const hasShow = (day) =>
  allShows.value.some(s => {
    const d = new Date(s.date)
    return inCurrentMonth(s.date) && d.getDate() === day
  })

const showLabel = (dateStr) =>
  new Intl.DateTimeFormat('fi', { weekday: 'long', day: 'numeric', month: 'numeric' }).format(new Date(dateStr))

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)
</script>

<style scoped>
.block-calendar {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--offset);
  align-items: start;
}

.month-nav {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.month-nav button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  opacity: 0.5;
}

.month-nav button:hover { opacity: 1; }

.month-name {
  font-weight: bold;
  font-size: 1rem;
}

.weekdays,
.days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  text-align: center;
}

.weekdays span {
  font-size: 0.75rem;
  opacity: 0.5;
  padding: 0.4rem 0;
}

.day {
  padding: 0.4rem;
  font-size: 0.9rem;
  border-radius: 50%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day.active {
  outline: 1.5px solid currentColor;
}

.shows-label {
  font-size: 0.85rem;
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1rem;
}

.shows-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.show-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: baseline;
  text-decoration: none;
}

.show-row:hover .show-title { text-decoration: underline; }

.show-time {
  font-size: 0.9rem;
  opacity: 0.6;
}

.no-shows {
  opacity: 0.4;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .block-calendar { grid-template-columns: 1fr; }
}
</style>
